import { IconExternalLink } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute, linkOptions, Outlet } from "@tanstack/react-router";
import z from "zod";
import { SiteHeader } from "@/components/site-header";
import { StatusBadge } from "@/components/status-badge";
import { TabBar, type TabBarTabs } from "@/components/tab-bar";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Tabs } from "@/components/ui/tabs";
import { getChannelStatus } from "@/enums/channel-status";
import { getDashboardUrl } from "@/lib/url";
import { orpc } from "@/orpc/client";

export const Route = createFileRoute("/_app/channels/$channelId")({
	component: Page,
	params: z.object({
		channelId: z.coerce.number(),
	}),
	async loader({ context, params }) {
		context.queryClient.prefetchQuery(
			orpc.channels.show.queryOptions({
				input: {
					channelId: params.channelId,
				},
			}),
		);
	},
});

function Page() {
	const params = Route.useParams();

	const { data } = useSuspenseQuery(
		orpc.channels.show.queryOptions({
			input: {
				channelId: params.channelId,
			},
		}),
	);

	const tabs: TabBarTabs = linkOptions([
		{
			key: "details",
			to: "/channels/$channelId",
			params: { channelId: data.ID },
			activeOptions: { exact: true },
			label: "Details",
		},
		{
			key: "monetization",
			to: "/channels/$channelId/monetization",
			params: { channelId: data.ID },
			label: "Monetization",
		},
		{
			key: "audio",
			to: "/channels/$channelId/audio",
			params: { channelId: data.ID },
			label: "Audio",
		},
		{
			key: "jingles",
			to: "/channels/$channelId/jingles",
			params: { channelId: data.ID },
			label: "Jingles",
		},
		{
			key: "team",
			to: "/channels/$channelId/team",
			params: { channelId: data.ID },
			label: "Team",
			count: data.usersCount,
		},
		{
			key: "directories",
			to: "/channels/$channelId/directories",
			params: { channelId: data.ID },
			label: "Directories",
		},
		{
			key: "episodes",
			to: "/channels/$channelId/episodes",
			params: { channelId: data.ID },
			label: "Episodes",
			count: data.episodesCount,
		},
		{
			key: "files",
			to: "/channels/$channelId/files",
			params: { channelId: data.ID },
			label: "Files",
		},
		{
			key: "settings",
			to: "/channels/$channelId/settings",
			params: { channelId: data.ID },
			label: "Settings",
		},
	]);

	return (
		<>
			<SiteHeader
				breadcrumbs={[
					{
						key: "channels",
						label: "Channels",
						linkProps: { to: "/channels" },
					},
					{
						key: "channel",
						label: data.Title ?? "Channel Details",
					},
				]}
			/>
			<div className="flex flex-1 flex-col">
				<div className="@container/main flex flex-1 flex-col gap-2">
					<div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
						<div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6">
							<div className="w-full flex flex-col justify-between gap-2 md:gap-4 md:flex-row lg:items-end">
								<div className="flex gap-3">
									<Avatar className="size-16">
										<AvatarImage
											src={
												data.Icon?.ThumbnailURL
													? `${import.meta.env.VITE_CDN_URL}${data.Icon.ThumbnailURL}`
													: undefined
											}
										/>
										<AvatarFallback>
											{data.Title?.slice(0, 1).toUpperCase()}
										</AvatarFallback>
									</Avatar>
									<div>
										<h1 className="text-lg font-semibold leading-tight">
											{data.Title}{" "}
											<StatusBadge status={getChannelStatus(data.StatusID)} />
										</h1>
										<p className="text-sm text-muted-foreground">
											{data.HomepageUrl}
										</p>
										<p className="text-sm text-muted-foreground">
											Channel ID: #{data.ID}
										</p>
									</div>
								</div>
								<div className="flex items-center gap-2 justify-end">
									<Button
										variant="outline"
										size="sm"
										nativeButton={false}
										render={
											<a
												href={getDashboardUrl(
													`/podcasts/${data.UID}/settings/details`,
												)}
												target="_blank"
												rel="noopener noreferrer"
											>
												<IconExternalLink />
												Open in Dashboard
											</a>
										}
									/>
								</div>
							</div>
						</div>
						<Tabs
							onValueChange={(e) => {
								console.log("tabs changed", e);
							}}
						>
							<div className="px-4 lg:px-6">
								<TabBar tabs={tabs} />
							</div>
							<Outlet />
						</Tabs>
					</div>
				</div>
			</div>
		</>
	);
}
